import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Target,
  Trophy,
  ChevronRight,
  Loader2,
  FileQuestion
} from 'lucide-react';

const History = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(`${API}/exams/history`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });

        if (response.ok) {
          const data = await response.json();
          setAttempts(data.attempts || []);
        }
      } catch (error) {
        toast.error('Failed to load history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [getAuthHeaders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100 dark:bg-green-950/30';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-950/30';
    return 'text-red-600 bg-red-100 dark:bg-red-950/30';
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 'training': return 'Training';
      case 'exam': return 'Exam';
      case 'custom': return 'Custom';
      default: return mode;
    }
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case 'training': return 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400';
      case 'exam': return 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400';
      case 'custom': return 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="font-semibold text-lg">Practice History</h1>
            <div className="w-20" /> {/* Spacer */}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {attempts.length === 0 ? (
          <Card className="bg-white dark:bg-slate-900">
            <CardContent className="py-16 text-center">
              <FileQuestion className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h2 className="text-xl font-semibold mb-2">No Practice History</h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Start practicing to see your results here
              </p>
              <Button 
                onClick={() => navigate('/training')}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="start-practicing-btn"
              >
                Start Practicing
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {attempts.map((attempt) => (
              <Card 
                key={attempt.attempt_id}
                className="bg-white dark:bg-slate-900 hover:border-orange-500/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/results/${attempt.attempt_id}`)}
                data-testid={`attempt-${attempt.attempt_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getModeColor(attempt.mode)}`}>
                          {getModeLabel(attempt.mode)}
                        </span>
                        <span className="font-semibold">{attempt.track || attempt.exam_type}</span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(attempt.completed_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {attempt.results?.total_questions || attempt.question_count || 0} questions
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTime(attempt.results?.time_used)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className={`px-4 py-2 rounded-xl font-bold ${getScoreColor(attempt.results?.score || 0)}`}>
                        {(attempt.results?.score || 0).toFixed(0)}%
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default History;
